import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, Loader2, Search, X } from '../../icons';
import { Avatar } from '../../components/shared/Avatar';
import {
  expandToPersonIds, filterGroups, peopleOf,
  type Picked, type PickerGroup, type PickerPerson,
} from '../../components/shared/EntityPicker';
import { tCalendar } from '../../lib/calendar-i18n';
import { AttendeeContactsTab } from './AttendeeContactsTab';
import { externalDisplayName, externalPickKey, pickFromLine, type ContactLine, type ExternalPick } from './lib/externalAttendees';
import './EventCreateSide.css';
import './AttendeeContacts.css';

/**
 * 참석자 고르기 옆 창 (2026-10-19 · 사용자가 시안 「확정안 ②」로 고름).
 *
 * 일정 추가 창 «오른쪽에 나란히» 붙는다. 왼쪽 = 이름·팀 검색 + 팀 트리(「팀 전체」), 오른쪽 = 고른 사람.
 * 「n명으로 정하기」를 눌러야 일정 창에 들어간다 — ✕·Esc 는 고른 것을 버리고 닫는다.
 * 나(작성자)는 늘 들어가고 뺄 수 없다(서버도 그렇게 넣는다 — withCreatorAttendee).
 * 고른 결과는 «사람 단위»로 넘긴다(팀째 고른 것도 펼친다) — 저장 때 펼치는 expandToPersonIds 와 같은 결과다.
 */
interface Props {
  groups: ReadonlyArray<PickerGroup>;
  loose: ReadonlyArray<PickerPerson>;
  loading: boolean;
  /** 지금 일정 창의 참석자(사람·팀 섞여도 된다). */
  value: ReadonlyArray<Picked>;
  selfId: string | null;
  selfName?: string | null;
  max: number;
  /**
   * 팀 캘린더면 「팀원 모두 초대」 단추를 위에 둔다(종전 TeamInviteButton). 고르는 중의 명단(existingIds)을 받아
   * 더한 사람을 «이 창의» 명단에 넣는다(add) — 「정하기」 전까지 일정 창에는 안 들어간다.
   */
  renderToolbar?: (ctx: { existingIds: readonly string[]; add: (people: Picked[]) => void }) => ReactNode;
  /**
   * ⭐ 2026-11-02 (지시 57) — 외부 참석자(주소록). 주면 왼쪽에 「조직도 · 주소록」 탭이 붙고, 「정하기」가 고른 외부 참석자를
   *   둘째 인자로 넘긴다. ⛔ 안 주면 종전과 한 글자도 같다(탭 없음 · 둘째 인자 없음) — 옛 서버·특수 일정.
   *   lockedNote 가 있으면 주소록 체크를 잠그고 그 까닭을 보인다.
   */
  external?: { value: ReadonlyArray<ExternalPick>; max: number; lockedNote?: string | null };
  onApply: (people: Picked[], external?: ExternalPick[]) => void;
  onClose: () => void;
}

export function AttendeePickerPanel({ groups, loose, loading, value, selfId, selfName, max, renderToolbar, external, onApply, onClose }: Props) {
  // ⭐ 2026-11-02 (지시 57) — 「조직도 · 주소록」 탭. 주소록은 «처음 열 때» 붙이고(그때 부른다), 그 뒤로는 숨기기만 한다.
  const [tab, setTab] = useState<'org' | 'contacts'>('org');
  const [contactsOpened, setContactsOpened] = useState(false);
  const [ext, setExt] = useState<ExternalPick[]>(() => [...(external?.value ?? [])]);
  /*
   * ⭐ 2026-11-02 (지시 57E) — 수정 화면에서 상세(외부 명단)를 받기 «전에» 창을 열면 `external` 이 없는 채로 열린다.
   *   그 뒤 상세가 오면 그때 «한 번» 받은 명단으로 채운다 — 안 채우면 빈 명단으로 「정하기」가 되어 외부 참석자가 모두 빠진다.
   *   처음부터 있던 판은 손대지 않는다(고르는 중인 값을 덮지 않는다).
   */
  const extSeededRef = useRef(external !== undefined);
  useEffect(() => {
    if (extSeededRef.current || !external) return;
    extSeededRef.current = true;
    setExt([...external.value]);
  }, [external]);
  const extPicked = useMemo(
    () => new Set(ext.map((p) => p.contactId).filter((v): v is string => typeof v === 'string')),
    [ext],
  );
  // ⭐ 49D — 원래 명단에 있던 연락처를 껐다 다시 켜면 그 사람(외부 참석자 id)을 되살린다 — 새 사람으로 보내지 않는다.
  const toggleExternal = (line: ContactLine) => {
    if (external?.lockedNote) return;
    setExt((cur) => (cur.some((p) => p.contactId === line.contactId)
      ? cur.filter((p) => p.contactId !== line.contactId)
      : [...cur, pickFromLine(line, external?.value ?? [])]));
  };
  const extOver = external ? ext.length > external.max : false;
  const openTab = (t: 'org' | 'contacts') => {
    setTab(t);
    if (t === 'contacts') setContactsOpened(true);
  };

  /** 사람 id → 줄(이름·소속). 조직도 전체에서 한 번 만든다. */
  const personIndex = useMemo(() => {
    const m = new Map<string, PickerPerson>();
    const walk = (list: ReadonlyArray<PickerGroup>) => {
      for (const g of list) { for (const p of g.people ?? []) if (!m.has(p.id)) m.set(p.id, p); walk(g.children ?? []); }
    };
    walk(groups);
    for (const p of loose) if (!m.has(p.id)) m.set(p.id, p);
    return m;
  }, [groups, loose]);

  // 고르는 중의 명단 — 처음은 일정 창의 값(팀은 펼쳐서).
  const [picked, setPicked] = useState<string[]>(() => {
    const ids = expandToPersonIds(value, groups as PickerGroup[]);
    if (selfId && !ids.includes(selfId)) ids.unshift(selfId);
    return ids;
  });
  const pickedSet = useMemo(() => new Set(picked), [picked]);
  /** 조직도 밖에서 들어온 이름(팀 초대 단추 · 일정 창에 이미 있던 사람). */
  const [extraNames, setExtraNames] = useState<Map<string, string>>(
    () => new Map(value.filter((v) => v.kind === 'person').map((v) => [v.id, v.name])),
  );
  const [query, setQuery] = useState('');
  // 펼친 팀 — 처음에는 «나»가 있는 팀만.
  const [open, setOpen] = useState<Set<string>>(() => {
    const s = new Set<string>();
    if (!selfId) return s;
    const walk = (list: ReadonlyArray<PickerGroup>, trail: string[]): boolean => {
      for (const g of list) {
        const here = [...trail, g.id];
        if ((g.people ?? []).some((p) => p.id === selfId) || walk(g.children ?? [], here)) { here.forEach((id) => s.add(id)); return true; }
      }
      return false;
    };
    walk(groups, []);
    return s;
  });

  const q = query.trim();
  const shownGroups = useMemo(() => filterGroups(groups, q), [groups, q]);
  const shownLoose = useMemo(
    () => (q ? loose.filter((p) => [p.name, p.sub, p.keywords].filter(Boolean).join(' ').toLowerCase().includes(q.toLowerCase())) : loose),
    [loose, q],
  );

  const toggle = (id: string) => {
    if (id === selfId) return;
    setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };
  const toggleTeam = (g: PickerGroup) => {
    const ids = peopleOf(g).filter((p) => !p.disabled).map((p) => p.id);
    const all = ids.length > 0 && ids.every((id) => pickedSet.has(id));
    setPicked((cur) => (all
      ? cur.filter((id) => id === selfId || !ids.includes(id))
      : [...cur, ...ids.filter((id) => !cur.includes(id))]));
  };
  const nameOf = (id: string) => personIndex.get(id)?.name ?? extraNames.get(id) ?? (id === selfId ? (selfName ?? tCalendar('calendar.attendee_panel.me')) : tCalendar('calendar.attendee.unknown'));
  const over = picked.length > max;

  const renderGroup = (g: PickerGroup, depth: number): ReactNode => {
    const expanded = q ? true : open.has(g.id);
    const all = peopleOf(g).filter((p) => !p.disabled);
    const n = all.filter((p) => pickedSet.has(p.id)).length;
    const full = all.length > 0 && n === all.length;
    return (
      <li key={g.id} className="calendar-attpanel-group">
        <div className="calendar-attpanel-grouprow" style={{ paddingLeft: depth * 16 }}>
          <button
            type="button"
            className="calendar-attpanel-fold"
            aria-expanded={expanded}
            onClick={() => setOpen((cur) => { const s = new Set(cur); if (s.has(g.id)) s.delete(g.id); else s.add(g.id); return s; })}
          >
            {expanded ? <ChevronDown size={13} aria-hidden /> : <ChevronRight size={13} aria-hidden />}
            <span className="calendar-attpanel-groupname">{g.name}</span>
            <span className="calendar-attpanel-groupcount">
              {tCalendar('calendar.attendee_panel.count', { n: all.length })}
              {n > 0 ? ` · ${tCalendar('calendar.attendee_panel.picked_n', { n })}` : ''}
            </span>
          </button>
          {all.length > 0 && (
            <button type="button" className="calendar-attpanel-teamall" onClick={() => toggleTeam(g)}>
              {tCalendar(full ? 'calendar.attendee_panel.team_none' : 'calendar.attendee_panel.team_all')}
            </button>
          )}
        </div>
        {expanded && (
          <ul className="calendar-attpanel-list">
            {(g.people ?? []).map((p) => renderPerson(p, depth + 1))}
            {(g.children ?? []).map((c) => renderGroup(c, depth + 1))}
          </ul>
        )}
      </li>
    );
  };
  const renderPerson = (p: PickerPerson, depth: number): ReactNode => {
    const me = p.id === selfId;
    return (
      <li key={p.id}>
        <label className="calendar-attpanel-person" style={{ paddingLeft: depth * 16 + 4 }}>
          <input
            type="checkbox"
            checked={pickedSet.has(p.id) || me}
            disabled={me || (p.disabled && !pickedSet.has(p.id))}
            onChange={() => toggle(p.id)}
          />
          <span aria-hidden><Avatar userId={Number(p.id)} name={p.name} size="xs" /></span>
          <span className="calendar-attpanel-pname">
            {p.name}{me ? ` (${tCalendar('calendar.attendee_panel.me')})` : ''}
          </span>
          {p.sub && <span className="calendar-attpanel-psub">{p.sub}</span>}
        </label>
      </li>
    );
  };

  return (
    <div
      className="calendar-side-panel calendar-attpanel"
      role="dialog"
      aria-labelledby="calendar-attpanel-title"
      onKeyDown={(e) => {
        // 일정 창까지 닫히지 않게 — useEscapeClose 는 defaultPrevented 인 Esc 를 넘긴다(RoomStatusModal 과 같다).
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
      }}
    >
      <header className="calendar-side-head">
        <h3 id="calendar-attpanel-title">{tCalendar('calendar.attendee_panel.title')}</h3>
        <button type="button" className="calendar-side-x" onClick={onClose} aria-label={tCalendar('calendar.btn.close')}>
          <X size={18} />
        </button>
      </header>

      <div className="calendar-attpanel-body">
        <div className="calendar-attpanel-pick">
          {external && (
            <div className="calendar-attpanel-tabs" role="tablist" aria-label={tCalendar('calendar.attendee_panel.tabs')}>
              {(['org', 'contacts'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  id={`calendar-attpanel-tab-${t}`}
                  aria-selected={tab === t}
                  aria-controls={`calendar-attpanel-pane-${t}`}
                  className="calendar-attpanel-tab"
                  data-testid={`attendee-tab-${t}`}
                  onClick={() => openTab(t)}
                >
                  {tCalendar(t === 'org' ? 'calendar.attendee_panel.tab_org' : 'calendar.attendee_panel.tab_contacts')}
                </button>
              ))}
            </div>
          )}
          {external && contactsOpened && (
            <div
              className="calendar-attpanel-tabpane"
              role="tabpanel"
              id="calendar-attpanel-pane-contacts"
              aria-labelledby="calendar-attpanel-tab-contacts"
              hidden={tab !== 'contacts'}
            >
              <AttendeeContactsTab picked={extPicked} onToggle={toggleExternal} lockedNote={external.lockedNote ?? null} />
            </div>
          )}
          <OrgPane on={Boolean(external)} hidden={tab !== 'org'}>
          <label className="calendar-attpanel-search">
            <Search size={14} aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tCalendar('calendar.attendee_panel.search')}
              aria-label={tCalendar('calendar.attendee_panel.search')}
            />
          </label>
          {renderToolbar?.({ existingIds: picked, add: (people) => {
            setExtraNames((m) => { const n = new Map(m); for (const x of people) n.set(x.id, x.name); return n; });
            setPicked((cur) => [...cur, ...people.map((x) => x.id).filter((id) => !cur.includes(id))]);
          } })}
          <div className="calendar-attpanel-tree">
            {loading ? (
              <p className="calendar-attpanel-msg"><Loader2 size={13} className="calendar-spin" aria-hidden /> {tCalendar('calendar.attendee_panel.loading')}</p>
            ) : shownGroups.length === 0 && shownLoose.length === 0 ? (
              <p className="calendar-attpanel-msg">{tCalendar('calendar.attendee_panel.empty')}</p>
            ) : (
              <ul className="calendar-attpanel-list">
                {shownGroups.map((g) => renderGroup(g, 0))}
                {shownLoose.length > 0 && (
                  <li className="calendar-attpanel-group">
                    <div className="calendar-attpanel-grouprow"><span className="calendar-attpanel-groupname">{tCalendar('calendar.attendee_panel.loose')}</span></div>
                    <ul className="calendar-attpanel-list">{shownLoose.map((p) => renderPerson(p, 1))}</ul>
                  </li>
                )}
              </ul>
            )}
          </div>
          </OrgPane>
        </div>

        <div className="calendar-attpanel-chosen">
          <div className="calendar-attpanel-chosenhead">
            <span>{tCalendar('calendar.attendee_panel.selected', { n: picked.length + ext.length })}</span>
            {(picked.some((id) => id !== selfId) || (ext.length > 0 && !external?.lockedNote)) && (
              <button
                type="button"
                onClick={() => {
                  setPicked(selfId ? [selfId] : []);
                  if (!external?.lockedNote) setExt([]);
                }}
              >
                {tCalendar('calendar.attendee_panel.clear')}
              </button>
            )}
          </div>
          <ul className="calendar-attpanel-chosenlist">
            {picked.map((id) => (
              <li key={id}>
                <span aria-hidden><Avatar userId={Number(id)} name={nameOf(id)} size="xs" /></span>
                <span className="calendar-attpanel-pname">
                  {nameOf(id)}{id === selfId ? <span className="calendar-attpanel-psub"> {tCalendar('calendar.attendee_panel.me')}</span> : null}
                </span>
                {id !== selfId && (
                  <button type="button" className="calendar-attpanel-remove" onClick={() => toggle(id)} aria-label={tCalendar('calendar.attendee_panel.remove', { name: nameOf(id) })}>
                    <X size={12} aria-hidden />
                  </button>
                )}
              </li>
            ))}
            {/* ⭐ 2026-11-02 (지시 57) — 고른 외부 참석자. 직원과 구분해 「외부」 꼬리표 + 회사. */}
            {ext.map((p) => (
              <li key={`ext:${externalPickKey(p)}`} data-testid="attendee-chosen-external">
                <span className="calendar-external-badge">{tCalendar('calendar.attendee_panel.external_badge')}</span>
                <span className="calendar-attpanel-pname">
                  {externalDisplayName(p)}
                  {p.company && <span className="calendar-attpanel-chosensub">{p.company}</span>}
                </span>
                {!external?.lockedNote && (
                  <button
                    type="button"
                    className="calendar-attpanel-remove"
                    onClick={() => setExt((cur) => cur.filter((x) => externalPickKey(x) !== externalPickKey(p)))}
                    aria-label={tCalendar('calendar.attendee_panel.remove', { name: externalDisplayName(p) })}
                  >
                    <X size={12} aria-hidden />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <footer className="calendar-side-foot">
        <span className={`calendar-attpanel-max${over || extOver ? ' is-over' : ''}`} role={over || extOver ? 'alert' : undefined}>
          {over
            ? tCalendar('calendar.event_create.err_attendee_max', { max, n: picked.length })
            : extOver && external
              ? tCalendar('calendar.attendee_panel.external_max', { max: external.max, n: ext.length })
              : tCalendar('calendar.attendee_panel.max', { max })}
        </span>
        <button
          type="button"
          className="calendar-side-ok"
          disabled={over || extOver}
          onClick={() => {
            const people = picked.map((id): Picked => {
              const p = personIndex.get(id);
              return { kind: 'person', id, name: p?.name ?? nameOf(id), ...(p?.sub ? { sub: p.sub } : {}) };
            });
            // 외부 참석자를 받는 창에서만 둘째 인자를 넘긴다 — 안 받는 창은 종전과 같은 호출이다.
            if (external) onApply(people, ext);
            else onApply(people);
          }}
        >
          {tCalendar('calendar.attendee_panel.apply', { n: picked.length + ext.length })}
        </button>
      </footer>
    </div>
  );
}

/** 「조직도」 탭 칸 — 탭이 없으면(외부 참석자 미지원) 감싸지 않아 DOM 이 종전과 같다. */
function OrgPane({ on, hidden, children }: { on: boolean; hidden: boolean; children: ReactNode }) {
  if (!on) return <>{children}</>;
  return (
    <div
      className="calendar-attpanel-tabpane"
      role="tabpanel"
      id="calendar-attpanel-pane-org"
      aria-labelledby="calendar-attpanel-tab-org"
      hidden={hidden}
    >
      {children}
    </div>
  );
}
